import * as React from "react";
import { observer } from "mobx-react";
import { UitkCardAloha, UitkCardMedia, UitkCardAlohaLink, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkBadge, UitkVipBadge } from "uitk-react-badge";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { withStores } from "stores";
import { RouteStore } from "bernie-core";
import { Link } from "react-router-dom";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export interface CompactHotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  border?: boolean;
  routeStore: RouteStore;
  priceDisclaimerLabel: string;
  strikeThroughPrice: string;
  disclaimerInfoDialog: () => JSX.Element;
  currentPrice: string;
  a11yPrice: string;
  cardClicked: (x: MabTrackEvent) => null;
}

export const CompactHotelInventoryCard = withMabTracker(
  withStores("routeStore")(
    observer(
      ({
        offer,
        cardIndex,
        campaignId,
        moduleName,
        border,
        routeStore,
        priceDisclaimerLabel,
        strikeThroughPrice,
        disclaimerInfoDialog,
        currentPrice,
        a11yPrice,
        cardClicked,
      }: CompactHotelInventoryCardProps) => {
        if (offer.__typename !== "HotelOfferItem") {
          return null;
        }

        return (
          <UitkCardAloha
            className="CompactHotelInventoryCard uitk-flex-justify-content-flex-start uitk-flex-column"
            border={border}
          >
            <UitkCardMedia>
              <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
                {offer.vipMessage && (
                  <UitkVipBadge className="all-t-margin-three all-l-margin-three hotelBadge" text={offer.vipMessage} />
                )}
                <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
              </UitkFigure>
            </UitkCardMedia>
            <div className="uitk-cell">
              <UitkLayoutPosition type="relative">
                <UitkCardAlohaContentSection>
                  {offer.discountBadge?.text && (
                    <div className="uitk-cell uitk-type-right badgeStyle">
                      <UitkBadge text={offer.discountBadge?.text} className="all-l-margin-three" />
                    </div>
                  )}
                  <UitkLayoutGrid columns={2} space="two" className="all-b-padding-two">
                    <div>
                      <h3 className="truncate all-b-padding-half pwa-theme--grey-900 uitk-type-heading-500">
                        {offer.name}
                      </h3>
                      <span className="uitk-type-300">{offer.location}</span>
                      {offer.freeCancellationMessage && (
                        <div className="uitk-cell all-t-padding-one uitk-type-300 freeCancellation">
                          {offer.freeCancellationMessage}
                        </div>
                      )}
                      {offer.guestReviewRating && (
                        <div className="uitk-cell all-r-padding-one reviewRating">
                          <span className="uitk-type-heading-300 uitk-cell all-r-padding-one">
                            {offer.guestReviewRating}
                          </span>
                          <span className="uitk-type-300 pwa-theme--grey-700">{offer.guestReviewCount}</span>
                        </div>
                      )}
                    </div>
                    <div className="uitk-cell uitk-type-right">
                      <UitkPriceLockup
                        lockupPrice={currentPrice}
                        priceSubtextFirst={offer.lodgingOffer?.price?.qualifierText}
                        priceSubtextStandard={[`${offer?.dates}`]}
                        a11yPrice={a11yPrice}
                        a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                        strikeThroughPrice={strikeThroughPrice}
                        strikeThroughTrigger={
                          <Link
                            to={{
                              pathname: routeStore.pathname,
                              search: routeStore.mergeQueryString(routeStore.search, {
                                pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}`,
                              }),
                            }}
                            className="disclaimerInfoIcon"
                          />
                        }
                      />
                      {disclaimerInfoDialog()}
                    </div>
                  </UitkLayoutGrid>
                </UitkCardAlohaContentSection>
                <UitkCardAlohaLink>
                  {offer.lodgingOffer && (
                    <TrackedLink
                      moduleName={moduleName}
                      action={Action.CLICK}
                      href={offer.lodgingOffer?.actionLink.href}
                      rfrr={`card-${campaignId}-${cardIndex}`}
                      onClick={cardClicked}
                    >
                      {offer.lodgingOffer?.actionLink.text}
                    </TrackedLink>
                  )}
                </UitkCardAlohaLink>
              </UitkLayoutPosition>
            </div>
          </UitkCardAloha>
        );
      }
    )
  )
);
